import { SupabaseClient } from '@supabase/supabase-js'
import { Database, TablesInsert, TablesUpdate } from "@/supabase/types"
import { fetchData, printObject } from '@/lib/utils'
import { KeyValues } from './tenants'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'

const logger = createIsomorphicLogger('db.tenantProviders')

export const getTenantProvidersByTenantId = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: tenantProviders, error } = await supabase
    .from("tenant_providers")
    .select("*, providers(internal_name)")
    .eq("tenant_id", tenantId)

  if (error) {
		logError(logger, error, 'Failed to fetch Tenant Providers by tenant ID', { tenantId })
    throw new Error(error.message)
  }

  return tenantProviders
}

export const getTenantProviderByProviderName = async (
	supabase: SupabaseClient<Database>,
	tenantId: string,
	providerName: string
) => {
  const { data: tenantProvider, error } = await supabase
    .from("tenant_providers")
    .select("*, providers!inner(name)")
    .eq("tenant_id", tenantId)
		.eq("providers.internal_name", providerName)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Tenant Provider by Provider name', { tenantId, providerName })
    throw new Error(error.message)
  }

  return tenantProvider
}

export const getBillableApiKeys = async (supabase: SupabaseClient<Database>, tenantId: string): Promise<KeyValues | undefined> => {
	const billableProviders = await getTenantProvidersByTenantId(supabase, tenantId)
	if (billableProviders) {
		const aidonKeys = await fetchData(`/keys/aidon`)
		const billableKeys: KeyValues = {}

		for (const provider of billableProviders) {
			const providerKey:string | undefined = provider.providers?.internal_name
			if (providerKey) {
				if (provider.api_key && provider.api_key.length > 0) {
					billableKeys[providerKey] = provider.api_key
				}
				else if (!!aidonKeys.tenantKeyMap[providerKey]) {
					billableKeys[providerKey] = aidonKeys.tenantKeyMap[providerKey]
				}
				else if (!!aidonKeys.tenantSharedKeyMap[providerKey]) {
					billableKeys[providerKey] = aidonKeys.tenantSharedKeyMap[providerKey]
				}
			}
		}
		return billableKeys
	}

	return undefined
}

export const createTenantProvider = async (
	supabase: SupabaseClient<Database>,
  tenantProvider: TablesInsert<"tenant_providers">
) => {
  const { data: createdTenantProvider, error } = await supabase
    .from("tenant_providers")
    .insert(tenantProvider)
    .select("*")
		.single()

  if (!createdTenantProvider) {
		logError(logger, error, 'Failed to create Tenant Provider', { tenantProvider: printObject(tenantProvider) })
    throw new Error(error.message)
  }

  logger.info('Tenant Provider created successfully', { createdTenantProvider: printObject(createdTenantProvider) })
  return createdTenantProvider
}

export const updateTenantProvider = async (
	supabase: SupabaseClient<Database>,
  tenantId: string,
  providerId: string,
  tenantProvider: TablesUpdate<"tenant_providers">
) => {
  const { data: updatedTenant, error } = await supabase
    .from("tenant_providers")
    .update(tenantProvider)
    .eq("tenant_id", tenantId)
    .eq("provider_id", providerId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update Tenant Provider', { tenantId, providerId, tenantProvider: printObject(tenantProvider) })
    throw new Error(error.message)
  }

  return updatedTenant
}

export const deleteTenantProvider = async (
	supabase: SupabaseClient<Database>,
  tenantId: string,
  providerId: string
) => {
  const { error } = await supabase
    .from("tenant_providers")
    .delete()
    .eq("tenant_id", tenantId)
    .eq("provider_id", providerId)

  if (error) {
		logError(logger, error, 'Failed to delete Tenant Provider', { tenantId, providerId })
    throw new Error(error.message)
	}

  return true
}
